// file: pharmakon/client/src/config/theme.js
/**
 * @fileoverview PHARMAKON — legacy palette bridge (Campaign 3 migration)
 *
 * `T` used to carry two hardcoded hex palettes; every legacy page resolved
 * `T[dark ? "dark" : "light"]` itself, which froze those pages out of the
 * theme system (and out of the Pro/Novera third variant entirely).
 *
 * Both palettes are now the SAME token-emitting map: every key resolves to a
 * `var(--token)` from index.css, so the ~19 legacy consumers (dashboards,
 * achat components, ToastDisplay, legacy ui kit…) follow ALL themes live —
 * light, dark and Pro — with their markup untouched.
 *
 * Two caveats for consumers:
 *  · String math on these values (alpha suffixes like `${c.accent}55`) no
 *    longer works — use the matching -soft / -line token instead.
 *  · SVG presentation ATTRIBUTES (recharts `fill`/`stroke` props) do not
 *    resolve var(): use `resolveChartColors()` below for chart props.
 */

const TOKEN_PALETTE = {
  bg: "var(--bg)",
  bgAlt: "var(--bg-alt)",
  surf: "var(--surf)",
  surfUp: "var(--surf-up)",
  raised: "var(--raised)",
  border: "var(--border-subtle)",
  borderHi: "var(--border-default)",
  accent: "var(--accent)",
  accentDim: "var(--accent-dim)",
  accentGlow: "var(--accent-soft)",
  t0: "var(--text-0)",
  t1: "var(--text-1)",
  t2: "var(--text-2)",
  t3: "var(--text-3)",
  red: "var(--danger)",
  redDim: "var(--danger-soft)",
  amber: "var(--warning)",
  amberDim: "var(--warning-soft)",
  blue: "var(--info)",
  blueDim: "var(--info-soft)",
  // v3 has no teal token — maps to info, matching the Bdg teal→blue decision.
  teal: "var(--info)",
  tealDim: "var(--info-soft)",
  violet: "var(--purple)",
  violetDim: "var(--purple-soft)",
  shadow: "var(--shadow-card)",
  shadowUp: "var(--shadow-panel)",
};

export const T = {
  dark: TOKEN_PALETTE,
  light: TOKEN_PALETTE,
};

/**
 * Chart-prop colors — recharts forwards `fill`/`stroke` as SVG presentation
 * attributes, where `var()` does not resolve. Call inside a useMemo keyed on
 * the themeStore `theme` so charts re-color on switch:
 *   const chartC = useMemo(() => resolveChartColors(), [theme]);
 */
export const resolveChartColors = () => {
  if (typeof window === "undefined") return TOKEN_PALETTE;
  const cs = getComputedStyle(document.documentElement);
  const v = (name, fallback) => {
    const val = cs.getPropertyValue(name).trim();
    return val || fallback;
  };
  return {
    accent: v("--accent", "#0A6E35"),
    accentDim: v("--accent-dim", "rgba(10,110,53,0.08)"),
    blue: v("--info", "#1A60B5"),
    red: v("--danger", "#D93025"),
    amber: v("--warning", "#B86000"),
    violet: v("--purple", "#6B3FA0"),
    teal: v("--info", "#1A60B5"),
    border: v("--border-subtle", "rgba(8,90,45,0.1)"),
    surf: v("--surf", "#FFFFFF"),
    t0: v("--text-0", "#0B1F12"),
    t2: v("--text-2", "#517260"),
    t3: v("--text-3", "#A0BFAA"),
  };
};

export const BASE_CSS = (c) => `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
::-webkit-scrollbar{width:3px;height:3px}
::-webkit-scrollbar-thumb{background:${c.borderHi};border-radius:2px}
body{font-family:var(--font-ui);background:${c.bg};color:${c.t0};height:100vh;overflow:hidden}
.shell{display:flex;flex-direction:column;height:100vh;overflow:hidden;}
.topbar{height:52px;display:flex;align-items:center;padding:0 22px;gap:12px;background:${c.surf};border-bottom:1px solid ${c.border};flex-shrink:0;}
.tb-title{font-size:16px;font-weight:700;color:${c.t0};letter-spacing:-.2px;}
.tb-sub{font-size:10px;color:${c.t3};margin-top:1px;letter-spacing:.2px;}
.tb-right{display:flex;align-items:center;gap:8px;margin-left:auto;}
.content{flex:1;overflow-y:auto;padding:20px 22px;}
.card{background:${c.surf};border:1px solid ${c.border};border-radius:12px;box-shadow:${c.shadow};transition:all 120ms;overflow:hidden;}
.card:hover{box-shadow:${c.shadowUp};border-color:${c.borderHi};}
.card-head{padding:13px 18px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid ${c.border};}
.card-title{font-size:13px;font-weight:700;color:${c.t0};}
.card-sub{font-size:10px;color:${c.t3};margin-top:1px;}
.tbl{width:100%;border-collapse:collapse;}
.tbl th{padding:7px 14px;font-size:9.5px;font-weight:700;color:${c.t3};text-transform:uppercase;letter-spacing:.8px;text-align:left;border-bottom:1px solid ${c.border};background:${c.bgAlt};}
.tbl td{padding:9px 14px;font-size:12px;color:${c.t1};border-bottom:1px solid ${c.border};vertical-align:middle;}
.tbl tbody tr:last-child td{border-bottom:none;}
.tbl tbody tr:hover td{background:${c.accentDim};}
.mono{font-family:var(--font-mono);font-size:11.5px;}
.num{font-family:var(--font-mono);font-size:12px;font-weight:600;}
.bdg{display:inline-flex;align-items:center;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:700;letter-spacing:.2px;}
.bdg-g{background:${c.accentDim};color:${c.accent};}
.bdg-r{background:${c.redDim};color:${c.red};}
.bdg-a{background:${c.amberDim};color:${c.amber};}
.bdg-b{background:${c.blueDim};color:${c.blue};}
.bdg-t{background:${c.tealDim};color:${c.teal};}
.bdg-gr{background:${c.raised};color:${c.t2};border:1px solid ${c.border};}
.btn{display:inline-flex;align-items:center;gap:5px;border:none;cursor:pointer;border-radius:6px;font-family:var(--font-ui);font-weight:600;transition:all 120ms;white-space:nowrap;}
.btn-p{background:${c.accent};color:#fff;padding:7px 14px;font-size:12px;}
.btn-p:hover{filter:brightness(1.08);box-shadow:0 2px 12px ${c.accentGlow};}
.btn-s{background:${c.raised};color:${c.t1};border:1px solid ${c.border};padding:6px 12px;font-size:12px;}
.btn-s:hover{border-color:${c.borderHi};color:${c.t0};}
.btn-d{background:${c.redDim};color:${c.red};border:1px solid var(--danger-soft);padding:6px 12px;font-size:12px;}
.btn-d:hover{background:var(--danger-soft);}
.btn-ico{background:${c.raised};border:1px solid ${c.border};padding:6px 7px;color:${c.t2};border-radius:6px;cursor:pointer;display:flex;align-items:center;transition:all 120ms;}
.btn-ico:hover{background:${c.accentDim};color:${c.accent};border-color:${c.borderHi};}
.btn-sm{padding:5px 10px;font-size:11px;}
.tabs{display:flex;gap:1px;background:${c.raised};padding:2px;border-radius:7px;border:1px solid ${c.border};}
.tab{padding:4px 11px;border-radius:5px;font-size:11px;font-weight:600;cursor:pointer;color:${c.t2};transition:all 120ms;}
.tab.on{background:${c.surf};color:${c.t0};box-shadow:${c.shadow};}
.inp{background:${c.raised};border:1px solid ${c.border};border-radius:7px;padding:7px 11px;font-size:12.5px;color:${c.t0};font-family:var(--font-ui);outline:none;transition:all 120ms;}
.inp:focus{border-color:${c.accent};box-shadow:0 0 0 3px ${c.accentDim};}
.inp::placeholder{color:${c.t3};}
.fk{display:inline-flex;padding:2px 6px;background:${c.raised};border:1px solid ${c.borderHi};border-bottom:2px solid ${c.borderHi};border-radius:4px;font-family:var(--font-mono);font-size:9px;color:${c.t2};}
.live-dot{width:8px;height:8px;border-radius:50%;background:${c.accent};box-shadow:0 0 8px ${c.accent};animation:livepulse 2s infinite;flex-shrink:0;}
@keyframes livepulse{0%,100%{opacity:1}50%{opacity:.3}}
.hr{height:1px;background:${c.border};margin:10px 0;}
.flex{display:flex;}.col{flex-direction:column;}.ai-c{align-items:center;}.jc-sb{justify-content:space-between;}
.g4{gap:4px;}.g6{gap:6px;}.g8{gap:8px;}.g10{gap:10px;}.g12{gap:12px;}.g14{gap:14px;}.g16{gap:16px;}
.ml-a{margin-left:auto;}.f1{flex:1;min-width:0;}.w100{width:100%;}
.fw5{font-weight:500;}.fw6{font-weight:600;}.fw7{font-weight:700;}
.fs9{font-size:9px;}.fs10{font-size:10px;}.fs11{font-size:11px;}.fs12{font-size:12px;}.fs13{font-size:13px;}
.t0{color:${c.t0};}.t1{color:${c.t1};}.t2{color:${c.t2};}.t3{color:${c.t3};}
.tc{color:${c.accent};}.tr{color:${c.red};}.ta{color:${c.amber};}.tb{color:${c.blue};}.tt{color:${c.teal};}
.lsu{text-transform:uppercase;letter-spacing:.9px;}
.tac{text-align:center;}.tar{text-align:right;}.ow{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}
@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.anim{animation:fadeIn 220ms ease both;}
.a1{animation-delay:40ms;}.a2{animation-delay:80ms;}.a3{animation-delay:120ms;}.a4{animation-delay:160ms;}
@keyframes spin{to{transform:rotate(360deg)}}
`;

// Shared SVG icon component — use path keys
export const ICONS = {
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  search: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  trash:
    "M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2",
  edit: "M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z",
  save: "M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8",
  bell: "M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0",
  refresh:
    "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15",
  export: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12",
  chart: "M18 20V10M12 20V4M6 20v-6",
  up: "M12 19V5M5 12l7-7 7 7",
  dn: "M12 5v14M5 12l7 7 7-7",
  printer:
    "M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z",
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z",
  pill: "M10.5 3.5a4.5 4.5 0 016.364 6.364l-9 9A4.5 4.5 0 011.5 12.5l9-9z",
  cart: "M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0",
  login: "M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3",
  shield: "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z",
  package:
    "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  tag: "M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z",
  filter: "M22 3H2l8 9.46V19l4 2v-8.54L22 3z",
  chev_dn: "M6 9l6 6 6-6",
  chev_rt: "M9 18l6-6-6-6",
  spin_ico: "M21 12a9 9 0 11-6.219-8.56",
  live_dot: "M12 12m-3 0a3 3 0 106 0 3 3 0 10-6 0M2.05 12.05A10 10 0 1012 22",
  home: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2zM9 22V12h6v10",
  database:
    "M12 2C6.48 2 2 4.24 2 7s4.48 5 10 5 10-2.24 10-5-4.48-5-10-5zM2 7v10c0 2.76 4.48 5 10 5s10-2.24 10-5V7",
  brain:
    "M12 2a5 5 0 015 5 5 5 0 01-5 5 5 5 0 01-5-5 5 5 0 015-5M7 17l-2 4M17 17l2 4M7 17h10",
  ticket:
    "M15 5v2M15 11v2M15 17v2M5 5h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7a2 2 0 012-2z",
  credit:
    "M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2zM1 10h22",
  decision: "M22 12h-4l-3 9L9 3l-3 9H2",
  lock: "M5 11h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2zM7 11V7a5 5 0 0110 0v4",
  users: "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M13 7a4 4 0 11-8 0 4 4 0 018 0M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  clock: "M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2",
  inbox: "M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z",
  // Status/feedback glyphs (added for the v3 ERP migration — alerts/toasts).
  alert:
    "M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01",
  info: "M12 22a10 10 0 100-20 10 10 0 000 20zM12 16v-4M12 8h.01",
  check_circle: "M22 11.08V12a10 10 0 11-5.93-9.14M22 4 12 14.01l-3-3",
  x_circle: "M12 22a10 10 0 100-20 10 10 0 000 20zM15 9l-6 6M9 9l6 6",
  eye: "M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7zM12 15a3 3 0 100-6 3 3 0 000 6z",
};
